import { Link } from 'react-router-dom'
import {
  Sparkles, CheckCircle2, ArrowRight, Calendar, Star, Building2,
} from 'lucide-react'
import { PLANS, SUBSCRIPTION_FEATURES, SESSION_FEATURES, formatUSD, formatCOP } from '../constants/pricing'

/**
 * Public pricing page — no auth required. Wired into App.jsx as `/pricing`.
 *
 * Reads plan data from constants/pricing.js so prices stay in sync with the
 * dashboard and the MercadoPago Edge Function. The CTA always sends visitors
 * to /register (the actual checkout happens after sign-up so MP can attach
 * the payment to a user_id).
 */
export default function PricingPage() {
  const monthly = PLANS.monthly
  const annual  = PLANS.annual

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-brand-50 to-white">
      <PublicNav />

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-16 pb-10 text-center">
        <div className="inline-flex items-center gap-2 bg-accent-50 text-accent-700 text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-5">
          <Sparkles size={12} /> Plan estratégico guiado por IA
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
          Construye tu plan estratégico<br />
          <span className="text-brand-700">como lo haría un consultor senior.</span>
        </h1>
        <p className="text-base md:text-lg text-gray-500 max-w-2xl mx-auto leading-relaxed">
          Sigue la metodología de Next Valley Consultants — la misma que usamos con clientes corporativos —
          guiado paso a paso por un agente de IA. Sin contratar consultoría completa.
        </p>
      </section>

      {/* Subscription plans */}
      <section className="max-w-5xl mx-auto px-6 pb-12">
        <div className="grid md:grid-cols-2 gap-5">
          {/* Monthly */}
          <div className="rounded-2xl border-2 border-gray-200 bg-white p-7 hover:border-brand-300 hover:shadow-md transition-all">
            <p className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-1">Plan Mensual</p>
            <p className="text-4xl font-bold text-gray-900">{formatUSD(monthly.priceUSD)}<span className="text-base text-gray-400 font-normal">/mes</span></p>
            <p className="text-xs text-gray-400 mt-1">≈ {formatCOP(monthly.priceCOP)}/mes</p>
            <p className="text-sm text-gray-500 mt-3 leading-relaxed">{monthly.description}</p>
            <Link
              to="/register"
              className="mt-5 flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold text-sm py-3 rounded-xl transition-colors"
            >
              Empezar trial gratis <ArrowRight size={14} />
            </Link>
            <ul className="mt-5 space-y-2.5">
              {SUBSCRIPTION_FEATURES.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                  <CheckCircle2 size={15} className="text-emerald-500 flex-shrink-0 mt-0.5" />
                  {f}
                </li>
              ))}
            </ul>
          </div>

          {/* Annual */}
          <div className="rounded-2xl border-2 border-brand-700 bg-gradient-to-br from-brand-50 to-white p-7 shadow-lg relative">
            <span className="absolute -top-3 right-7 bg-brand-700 text-white text-[10px] font-bold uppercase tracking-wide px-3 py-1 rounded-full">
              {annual.badge ?? 'Recomendado'}
            </span>
            <p className="text-xs font-bold uppercase tracking-wide text-brand-700 mb-1">Plan Anual</p>
            <p className="text-4xl font-bold text-gray-900">{formatUSD(annual.priceUSD)}<span className="text-base text-gray-400 font-normal">/año</span></p>
            <p className="text-xs text-emerald-600 mt-1 font-semibold">≈ {formatCOP(annual.priceCOP)} · ahorras {annual.savings ?? '33%'} vs. mensual</p>
            <p className="text-sm text-gray-600 mt-3 leading-relaxed">{annual.description}</p>
            <Link
              to="/register"
              className="mt-5 flex items-center justify-center gap-2 bg-brand-700 hover:bg-brand-800 text-white font-semibold text-sm py-3 rounded-xl transition-colors"
            >
              Empezar trial gratis <ArrowRight size={14} />
            </Link>
            <ul className="mt-5 space-y-2.5">
              {SUBSCRIPTION_FEATURES.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                  <CheckCircle2 size={15} className="text-emerald-500 flex-shrink-0 mt-0.5" />
                  {f}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">
          Trial gratis sin tarjeta · Pago en COP a la TRM del día · Cancela cuando quieras
        </p>
      </section>

      {/* Sessions with consultant */}
      <section className="max-w-5xl mx-auto px-6 pb-16">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-3">
            <Calendar size={12} /> Sesiones 1 a 1 con consultor
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">¿Necesitas un experto al lado?</h2>
          <p className="text-gray-500 max-w-xl mx-auto leading-relaxed">
            Compra un pack de sesiones con un consultor NVC para revisar tu plan, validar decisiones críticas
            y resolver bloqueos. Las sesiones nunca caducan.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {['session_1', 'pack_3', 'pack_5', 'pack_10'].map(id => {
            const plan = PLANS[id]
            const perSession = Math.round(plan.priceUSD / plan.sessions)
            return (
              <div key={id} className="relative rounded-xl border border-gray-200 bg-white p-4">
                {plan.badge && (
                  <span className="absolute -top-2 right-3 text-[10px] font-bold uppercase tracking-wide bg-brand-700 text-white px-2 py-0.5 rounded-full">
                    {plan.badge}
                  </span>
                )}
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {plan.sessions} sesion{plan.sessions === 1 ? '' : 'es'}
                </p>
                <p className="text-xl font-bold text-gray-900 mt-1">{formatUSD(plan.priceUSD)}</p>
                <p className="text-[11px] text-gray-400 mt-0.5">≈ {formatCOP(plan.priceCOP)}</p>
                {plan.sessions > 1 && (
                  <p className="text-[11px] text-emerald-600 font-semibold mt-1">USD ${perSession}/sesión</p>
                )}
              </div>
            )
          })}
        </div>

        <ul className="mt-8 grid sm:grid-cols-2 gap-2 max-w-2xl mx-auto">
          {SESSION_FEATURES.map((f, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
              <CheckCircle2 size={15} className="text-emerald-500 flex-shrink-0 mt-0.5" />
              {f}
            </li>
          ))}
        </ul>
      </section>

      {/* Why */}
      <section className="bg-brand-700 text-white py-16">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl md:text-3xl font-bold mb-10 text-center">Por qué Next Valley</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <Star size={24} className="text-accent-400 mb-3" />
              <h3 className="font-semibold text-lg mb-1.5">Metodología real</h3>
              <p className="text-sm text-brand-100 leading-relaxed">
                Lo mismo que aplicamos con clientes corporativos por contrato de USD 50k+. No es un template
                hecho con plantillas de internet.
              </p>
            </div>
            <div>
              <Sparkles size={24} className="text-accent-400 mb-3" />
              <h3 className="font-semibold text-lg mb-1.5">IA que sabe de estrategia</h3>
              <p className="text-sm text-brand-100 leading-relaxed">
                Tu agente conoce las 7 etapas de la metodología NVC. No es un chatbot genérico — es un
                consultor entrenado.
              </p>
            </div>
            <div>
              <Building2 size={24} className="text-accent-400 mb-3" />
              <h3 className="font-semibold text-lg mb-1.5">Multi-mercado</h3>
              <p className="text-sm text-brand-100 leading-relaxed">
                Si tu empresa opera en varios mercados, cada uno tiene su análisis profundo
                (mercado, consumidor, competidores, estrategia) — no un plan único genérico.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 py-16">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center">Preguntas frecuentes</h2>
        <div className="space-y-5">
          {FAQ.map((q, i) => (
            <div key={i} className="rounded-xl border border-gray-200 p-5 bg-white">
              <h3 className="font-semibold text-gray-900 mb-1.5">{q.q}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{q.a}</p>
            </div>
          ))}
        </div>
      </section>

      <PublicFooter />
    </div>
  )
}

const FAQ = [
  {
    q: '¿Necesito ser una empresa grande?',
    a: 'No. La aplicación está pensada para empresas medianas (10–250 empleados) en Latinoamérica. Si eres más pequeño, el plan mensual te sirve para hacer tu primer plan formal sin contratar consultoría completa.',
  },
  {
    q: '¿Cuánto tiempo me toma terminar el plan?',
    a: 'Depende de qué tan completa sea la información que ya tienes. Como referencia: empresas con datos relativamente ordenados completan el plan en 4-6 semanas dedicando 3-5 horas por semana. La app guarda tu progreso, puedes ir al ritmo que necesites.',
  },
  {
    q: '¿Qué pasa si me empantano en una sección?',
    a: 'Tu agente de IA dentro del proyecto responde dudas estratégicas con contexto de tu plan. Si necesitas ayuda más profunda, compra un pack de sesiones 1 a 1 con un consultor NVC.',
  },
  {
    q: '¿Cómo funciona el trial?',
    a: 'Te registras gratis sin tarjeta y entras a tu proyecto inmediatamente. Puedes explorar todas las etapas, llenar contenido y ver cómo es la metodología. Cuando quieras descargar tu plan en Word/PDF sin marca de borrador, activas la suscripción.',
  },
  {
    q: '¿Puedo cancelar?',
    a: 'Sí, en cualquier momento. El plan mensual no tiene compromiso. Si cancelas, mantienes acceso hasta el final del período pagado.',
  },
  {
    q: '¿Mi información está segura?',
    a: 'Tu plan estratégico es tuyo. Lo guardamos cifrado, no lo compartimos con nadie ni lo usamos para entrenar modelos. Puedes exportar y borrar tu cuenta cuando quieras.',
  },
]

// ──────────────────────────────────────────────────────────────────────────
// Public layout chrome — shared with /terms, /privacy, /refunds
// ──────────────────────────────────────────────────────────────────────────
export function PublicNav() {
  return (
    <nav className="border-b border-gray-100 bg-white/80 backdrop-blur sticky top-0 z-30">
      <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-brand-700 flex items-center justify-center">
            <Sparkles size={16} className="text-accent-400" />
          </div>
          <span className="font-bold text-gray-900">Next Valley</span>
          <span className="text-gray-300 text-sm">·</span>
          <span className="text-sm text-gray-500">Estrategia en una Caja</span>
        </Link>
        <div className="flex items-center gap-1">
          <Link to="/pricing" className="text-sm text-gray-600 hover:text-brand-700 px-3 py-2 transition-colors">Precios</Link>
          <Link to="/login" className="text-sm text-gray-600 hover:text-brand-700 px-3 py-2 transition-colors">Iniciar sesión</Link>
          <Link
            to="/register"
            className="bg-brand-700 hover:bg-brand-800 text-white text-sm font-semibold px-4 py-2 rounded-xl transition-colors ml-1"
          >
            Empezar gratis
          </Link>
        </div>
      </div>
    </nav>
  )
}

export function PublicFooter() {
  return (
    <footer className="border-t border-gray-100 bg-white">
      <div className="max-w-6xl mx-auto px-6 py-10 text-sm text-gray-500">
        <div className="flex flex-col md:flex-row md:justify-between gap-4">
          <div>
            <p className="font-bold text-gray-900 mb-1">Next Valley Consultants</p>
            <p className="text-xs text-gray-400">Cali, Colombia · estrategia@nextvalleyco.com</p>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs">
            <Link to="/pricing"  className="hover:text-brand-700">Precios</Link>
            <Link to="/terms"    className="hover:text-brand-700">Términos y condiciones</Link>
            <Link to="/privacy"  className="hover:text-brand-700">Política de privacidad</Link>
            <Link to="/refunds"  className="hover:text-brand-700">Política de reembolsos</Link>
            <Link to="/login"    className="hover:text-brand-700">Iniciar sesión</Link>
          </div>
        </div>
        <p className="text-[11px] text-gray-400 mt-6">© {new Date().getFullYear()} Next Valley Consultants. Todos los derechos reservados.</p>
      </div>
    </footer>
  )
}
